import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Truck, CircleNotch, CheckCircle, XCircle, Crown } from '@phosphor-icons/react'
import { getMasterGrant, confirmMasterGrant } from '../services/users'

type Status = 'loading' | 'ready' | 'confirming' | 'success' | 'error'

export default function ConfirmMasterPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<Status>('loading')
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')

  const token = searchParams.get('token')

  useEffect(() => {
    async function load() {
      if (!token) {
        setStatus('error')
        setError('Link inválido. Token não encontrado.')
        return
      }
      try {
        const info = await getMasterGrant(token)
        if (info.used) {
          setStatus('error')
          setError('Este convite já foi utilizado.')
          return
        }
        if (info.expired) {
          setStatus('error')
          setError('Este convite expirou. Peça um novo a um administrador.')
          return
        }
        setEmail(info.email)
        setStatus('ready')
      } catch (err: any) {
        setStatus('error')
        setError(err.response?.data?.error || 'Convite inválido ou expirado.')
      }
    }
    load()
  }, [token])

  async function handleConfirm() {
    if (!token) return
    setStatus('confirming')
    try {
      await confirmMasterGrant(token)
      setStatus('success')
      setTimeout(() => navigate('/login'), 2500)
    } catch (err: any) {
      setStatus('error')
      setError(err.response?.data?.error || 'Não foi possível ativar o acesso.')
    }
  }

  return (
    <div className="w-full min-h-screen flex flex-col items-center justify-center" style={{ background: 'rgb(37, 48, 55)' }}>
      <div className="flex items-center gap-3 mb-8">
        <div className="rounded-xl p-3 shadow-lg" style={{ backgroundColor: 'rgb(5, 150, 105)' }}>
          <Truck size={32} weight="bold" className="text-white" />
        </div>
        <h1 className="text-3xl font-bold text-white">Eximtech</h1>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-slate-700/40 max-w-md w-full mx-4">
        {status === 'loading' && (
          <div className="text-center">
            <CircleNotch size={48} weight="bold" className="text-green-500 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Carregando convite...</h2>
            <p className="text-slate-400">Aguarde um momento</p>
          </div>
        )}

        {(status === 'ready' || status === 'confirming') && (
          <div className="text-center">
            <Crown size={48} weight="fill" className="text-purple-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Ativar acesso master</h2>
            <p className="text-slate-400 mb-6">
              Você foi convidado para ser <strong className="text-white">admin master</strong> com o email{' '}
              <strong className="text-white">{email}</strong>. Confirme para ativar.
            </p>
            <button
              onClick={handleConfirm}
              disabled={status === 'confirming'}
              className="w-full text-white font-medium py-2.5 px-4 rounded-lg transition-all hover:shadow-lg disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ backgroundColor: 'rgb(109, 40, 217)' }}
            >
              {status === 'confirming' ? (
                <>
                  <CircleNotch size={20} weight="bold" className="animate-spin" />
                  Ativando...
                </>
              ) : (
                <>
                  <Crown size={20} weight="fill" />
                  Ativar Acesso Master
                </>
              )}
            </button>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <CheckCircle size={48} weight="bold" className="text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Acesso master ativado!</h2>
            <p className="text-slate-400">Redirecionando para o login...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <XCircle size={48} weight="bold" className="text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Não foi possível ativar</h2>
            <p className="text-red-400 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full text-white font-medium py-2.5 px-4 rounded-lg transition-all hover:shadow-lg"
              style={{ backgroundColor: 'rgb(5, 150, 105)' }}
            >
              Ir para o login
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
